import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clientPortalApi } from '../../lib/http';
import ClientLayout from '../../components/ClientLayout';
import PageHeader from '../../components/client/PageHeader';
import CancelBookingModal from '../../components/client/bookings/CancelBookingModal';
import { formatDuration, formatPrice } from '../../utils/dateFormat';
import type { ClientBooking } from '../../types';

const BookingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { data: booking, isLoading, error } = useQuery({
    queryKey: ['client-booking', id],
    queryFn: async () => {
      const response = await clientPortalApi.bookings.get(id!);
      return response.data;
    },
    enabled: !!id,
  });

  const handleViewExpert = () => {
    if (booking) {
      navigate(`/u/experts/${booking.expert.id}`);
    }
  };

  const handleCancelClose = () => {
    setShowCancelModal(false);
    // Refresh booking data after cancel
    queryClient.invalidateQueries({ queryKey: ['client-booking', id] });
    queryClient.invalidateQueries({ queryKey: ['client-bookings'] });
    queryClient.invalidateQueries({ queryKey: ['client-bookings-preview'] });
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-10 h-10 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin" />
          <span className="mt-3 text-sm text-gray-500">Loading booking...</span>
        </div>
      </ClientLayout>
    );
  }

  if (error || !booking) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Booking not found</h2>
          <p className="text-sm text-gray-500 mb-4">This booking may have been deleted or doesn't exist.</p>
          <button
            onClick={() => navigate('/u/bookings')}
            className="px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors active:scale-95"
          >
            View All Bookings
          </button>
        </div>
      </ClientLayout>
    );
  }

  const date = booking.date_formatted;
  const time = booking.time_formatted;
  const isActiveBooking = booking.status === 'confirmed' || booking.status === 'pending';

  return (
    <ClientLayout>
      {/* Page Header */}
      <PageHeader title="Booking Details" backLabel="Go back" />

      <div className="space-y-4">
        {/* Pending Status Banner - Premium Design */}
        {booking.status === 'pending' && (
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border border-amber-200/60 shadow-sm">
            {/* Subtle animated background glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-100/0 via-amber-100/40 to-amber-100/0 animate-pulse" style={{ animationDuration: '3s' }} />

            <div className="relative p-4">
              <div className="flex items-center gap-3">
                {/* Animated icon container - matches expert avatar size (w-14 h-14) */}
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-amber-400/20 rounded-xl blur-md animate-pulse" style={{ animationDuration: '2s' }} />
                  <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white shadow-sm">
                      Awaiting Confirmation
                    </span>
                  </div>
                  <p className="text-sm text-amber-900/70 font-medium">
                    The expert will confirm your booking shortly
                  </p>
                </div>

                {/* Decorative element */}
                <div className="hidden sm:flex flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expert Info Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-lg shadow-sm">
              {getInitials(booking.expert.display_name)}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {booking.expert.display_name}
              </h3>
              <p className="text-sm text-purple-600 font-medium truncate">
                {booking.service?.name || 'Service N/A'}
              </p>
            </div>
            <button
              onClick={handleViewExpert}
              className="p-2.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors active:scale-95"
              aria-label="View expert profile"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Booking Details Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Appointment Details</h4>
          <div className="space-y-4">
            {/* Date */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Date</p>
                <p className="text-sm font-medium text-gray-900">{date}</p>
              </div>
            </div>

            {/* Time & Duration Row */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Time</p>
                <p className="text-sm font-medium text-gray-900">
                  {time}
                  {booking.service && (
                    <span className="text-gray-500 font-normal"> · {formatDuration(booking.service.duration_min)}</span>
                  )}
                </p>
              </div>
            </div>

            {/* Price */}
            {booking.service && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Price</p>
                  <p className="text-sm font-medium text-gray-900">{formatPrice(booking.service.price_cents, booking.service.currency)}</p>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Notes Card */}
        {booking.notes && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Notes</h4>
            <p className="text-sm text-gray-700">{booking.notes}</p>
          </div>
        )}

        {/* Cancellation Reason Card */}
        {booking.cancellation_reason && (
          <div className="bg-red-50 rounded-2xl border border-red-100 p-4">
            <h4 className="text-xs font-medium text-red-600 uppercase tracking-wider mb-2">Cancellation Reason</h4>
            <p className="text-sm text-red-700">{booking.cancellation_reason}</p>
          </div>
        )}

        {/* Cancel Appointment Link - only for active bookings that can be cancelled */}
        {isActiveBooking && booking.can_cancel && (
          <div className="text-center py-2">
            <button
              onClick={() => setShowCancelModal(true)}
              className="text-red-500 hover:text-red-600 text-sm font-medium transition-colors"
            >
              Cancel Appointment
            </button>
          </div>
        )}

        {/* Booked on info */}
        <p className="text-xs text-gray-400 text-center py-2">
          Booked on {new Date(booking.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* Cancel Modal */}
      {booking && (
        <CancelBookingModal
          booking={booking}
          isOpen={showCancelModal}
          onClose={handleCancelClose}
        />
      )}
    </ClientLayout>
  );
};

export default BookingDetail;
